import {
	type Maybe,
	parseThirdPartyARI,
	type SearchResult,
	type ThirdPartyRestrictionLevel,
} from '@atlassian/search-client';

import { is3pProductKey } from '../constants/products';
import {
	type ConnectorSource,
	ConnectorSources,
	isConnectorSource,
	type ThirdPartyConfigsBootstrap,
} from '../constants/schemas/3p-config';
import { VIEWED_EVENT } from '../ui/event-type-message';

import { isProductSmartlink } from './oauth-container';

export interface SearchResultGraphDocument extends SearchResult {
	excerpt: string;
	permissionLevel: Maybe<ThirdPartyRestrictionLevel>;
	subtype: string;
	integrationId: string;
	lastModifiedDate: string;
	initialContributors: {
		name: string;
	}[];
}

const GOOGLE_INTEGRATION_ARI = 'ari:cloud:platform::integration/google';
export const isGoogleDocument = (partial: SearchResult) =>
	partial.__typename === 'SearchResultGoogleDocument' ||
	(isGraphDocument(partial) &&
		partial.integrationId === GOOGLE_INTEGRATION_ARI &&
		partial.subtype === 'document');

export const isGraphDocument = (partial: SearchResult): partial is SearchResultGraphDocument =>
	partial.__typename === 'SearchResultGraphDocument';

export const getProductFromThirdPartyId = ({ thirdPartyId }: { thirdPartyId?: string }) => {
	try {
		if (thirdPartyId) {
			const { resourceOwner } = parseThirdPartyARI(thirdPartyId);
			const resourceType = resourceOwner.split('.').at(1);
			const productKey =
				resourceType === 'google-drive' || resourceType === 'google-drive-lite'
					? 'drive'
					: resourceType || '';
			if (is3pProductKey(productKey)) {
				return productKey;
			}
		}
		return undefined;
	} catch (e) {
		// If parsing fails, we default to undefined
		return undefined;
	}
};

const SMART_LINK_ENTITY_REGEX = /^ari:.*::remote-link/;

export const isAriSmartLink = (resultAri: string) => SMART_LINK_ENTITY_REGEX.test(resultAri);

export const isResultSmartLink = ({
	connectorType,
	resultAri,
	eventType,
	productKey,
	thirdPartyConfigs,
}: {
	resultAri: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	productKey?: string;
	eventType?: string;
	connectorType?: string | null;
}) =>
	connectorType === ConnectorSources.Smartlinks ||
	isAriSmartLink(resultAri) ||
	isProductSmartlink({ productKey, thirdPartyConfigs }) ||
	eventType === VIEWED_EVENT.SL_VIEWED;

const getProduct3PConfig = (
	productKey: string | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	return productKey && is3pProductKey(productKey) ? thirdPartyConfigs[productKey] : null;
};

/**
 * Returns the connectorType based on the parameters provided.
 * @param connectorType - This takes highest precedence and is used if it is valid and provided.
 * @param resultNodeId - This is used to determine if the result is a smartlink.
 * @param eventType - This is used to determine if the result is a smartlink.
 * @param productKey - This is used to find the third party config associated with productKey.
 * @param thirdPartyConfigs - This is used to find the third party config associated with productKey.
 * @returns
 */
export const getResultConnectorSource = ({
	connectorType,
	resultNodeId,
	eventType,
	productKey,
	thirdPartyConfigs,
}: {
	connectorType?: string | null;
	resultNodeId: string;
	eventType?: string;
	productKey?: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): ConnectorSource | undefined => {
	// Return the connectorType if it's already valid
	if (isConnectorSource(connectorType)) {
		return connectorType;
	}

	// connectorType will not exist for the following cases:
	// * when search GraphQL response returns null for connectorType
	// * when the given API doesn't return a connectorType. E.g. user documents.
	// as a result, we use fallback logic to determine the connectorType.

	if (
		isResultSmartLink({
			connectorType,
			resultAri: resultNodeId,
			eventType,
			productKey,
			thirdPartyConfigs,
		})
	) {
		return ConnectorSources.Smartlinks;
	}

	const config = getProduct3PConfig(productKey, thirdPartyConfigs);

	// No config found for the product
	if (!config) {
		return undefined;
	}

	if (config.isFederated) {
		return ConnectorSources.Federated;
	}

	// Return the single connector source if it's valid
	if (config.connectorSources?.length === 1) {
		const source = config.connectorSources[0];
		if (isConnectorSource(source)) {
			return source;
		}
	}

	// Default to Full for any other case where config exists
	return ConnectorSources.Full;
};
